import Link from '@alltrails/shared/denali/components/Link';
import Repeat from '@alltrails/shared/icons/Repeat';
import { defineMessages, FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import { reactivateSubscription as callReactivateSubscriptionApi, cancelSubscription as callCancelSubscriptionApi } from 'api/Purchases';
import CustomProvider from 'components/CustomProvider';
import NEW_PLAN_COUNTRY_CODES from 'constants/NewPlanPromptCountryCodes';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import useUser from 'hooks/useUser';
import RecurlyAccountStatus from 'types/RecurlyAccountStatus';
import { useEffect, useState } from 'react';
import logError from 'utils/logError';
import { LanguageSupportUtil } from 'utils/language_support_util';

import { ApiError } from '@alltrails/shared/api';
import * as styles from './AutoRenewal.module.scss';

type Props = {
  recurlyAccountStatus: RecurlyAccountStatus;
  nextTransactionAmount: string;
};

function useSubscriptionCancel() {
  const [isLoading, setIsLoading] = useState(false);
  const languageRegionCode = useLanguageRegionCode();

  const cancel = async (confirmText?: string | null) => {
    const shouldProceed = !confirmText || window.confirm(confirmText);

    if (shouldProceed) {
      setIsLoading(true);
      try {
        const apiResponse = await callCancelSubscriptionApi();
        if (apiResponse.operation === 'termination') {
          window.location.assign(LanguageSupportUtil.wrapUrlSafe(`/my/profile/edit?cancel_success=termination`, languageRegionCode));
        } else {
          window.location.assign(LanguageSupportUtil.wrapUrlSafe(`/my/profile/edit?cancel_success=cancellation`, languageRegionCode));
        }
      } catch (e) {
        let code: string;

        if (e instanceof ApiError && e.data?.errors?.length > 0) {
          code = e.data.errors[0].code;
        } else {
          code = 'generic_error';
        }

        window.location.assign(LanguageSupportUtil.wrapUrlSafe(`/my/profile/edit?cancel_fail=${code}`, languageRegionCode));
      }
    }
  };

  return { isLoading, cancel };
}

function useSubscriptionReactivate() {
  const [isLoading, setIsLoading] = useState(false);
  const languageRegionCode = useLanguageRegionCode();

  const reactivate = async (confirmText?: string | null) => {
    const shouldProceed = !confirmText || window.confirm(confirmText);

    if (shouldProceed) {
      setIsLoading(true);
      try {
        await callReactivateSubscriptionApi();
        window.location.assign(LanguageSupportUtil.wrapUrlSafe(`/my/profile/edit?reactivate_success=true`, languageRegionCode));
      } catch (e) {
        let code: string;

        if (e instanceof ApiError && e.data?.errors?.length > 0) {
          code = e.data.errors[0].code;
        } else {
          code = 'generic_error';
        }

        window.location.assign(LanguageSupportUtil.wrapUrlSafe(`/my/profile/edit?reactivate_fail=${code}`, languageRegionCode));
      }
    }
  };

  return { isLoading, reactivate };
}

function TurnOnAutoRenew({ nextTransactionAmount }: { nextTransactionAmount: string }) {
  const { ipCountry } = __AT_DATA__;
  const intl = useIntl();
  const user = useUser();
  const { isLoading, reactivate } = useSubscriptionReactivate();

  useEffect(() => {
    // I don't anticipate that nextTransactionAmount will ever be undefined here, but I'm adding error logging to monitor it just in case.
    if (!nextTransactionAmount) {
      logError('"nextTransactionAmount" is undefined in AutoRenew prompt');
    }
  }, []);

  const prompt = intl.formatMessage(
    {
      defaultMessage:
        'We\'re so glad that you\'re enjoying AllTrails+! By clicking "OK" you agree to renew your subscription at the current price of {annualCost} per year.'
    },
    {
      annualCost: nextTransactionAmount
    }
  );

  return (
    <Link
      onClick={async () => {
        if (NEW_PLAN_COUNTRY_CODES.includes(ipCountry) && !user?.subscription.trial) {
          reactivate(prompt);
        } else {
          reactivate(null);
        }
      }}
      className={styles.link}
      size="md"
      icon={{ Component: Repeat }}
      isButtonDisabled={isLoading}
    >
      <FormattedMessage defaultMessage="Turn on auto-renew" />
    </Link>
  );
}

function TurnOffAutoRenew() {
  const intl = useIntl();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const { isLoading, cancel } = useSubscriptionCancel();

  const formatter = new Intl.DateTimeFormat(languageRegionCode, { day: 'numeric', month: 'long', year: 'numeric' });

  const prompt = intl.formatMessage(
    {
      defaultMessage:
        'Are you sure? Your plan will expire on {date} and will not auto-renew. Click “OK” to proceed with turning off auto-renew or click “Cancel” to keep auto-renew on.'
    },
    {
      date: formatter.format(new Date(user.expirationDate))
    }
  );

  return (
    <Link
      onClick={() => {
        cancel(prompt);
      }}
      className={styles.link}
      size="md"
      icon={{ Component: Repeat }}
      isButtonDisabled={isLoading}
    >
      <FormattedMessage defaultMessage="Turn off auto-renew" />
    </Link>
  );
}

function TerminateSubscription() {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const { isLoading, cancel } = useSubscriptionCancel();

  const formatter = new Intl.DateTimeFormat(languageRegionCode, { day: 'numeric', month: 'long', year: 'numeric' });

  const prompt = intl.formatMessage(
    defineMessages({
      message: {
        defaultMessage:
          'Are you sure? Your AllTrails+ subscription expired on {date} and has not been renewed. If you wish to continue your subscription, please update your Billing Info.'
      }
    }).message,
    {
      date: formatter.format(new Date(user.expirationDate))
    }
  );

  return (
    <Link
      onClick={() => {
        cancel(prompt);
      }}
      className={styles.link}
      size="md"
      icon={{ Component: Repeat }}
      isButtonDisabled={isLoading}
    >
      <FormattedMessage defaultMessage="Cancel subscription" />
    </Link>
  );
}

function AutoRenewalContent({ recurlyAccountStatus, nextTransactionAmount }: Props) {
  switch (recurlyAccountStatus) {
    case RecurlyAccountStatus.Active:
      return <TurnOffAutoRenew />;
    case RecurlyAccountStatus.PastDue:
      return <TerminateSubscription />;
    case RecurlyAccountStatus.Cancelled:
      return <TurnOnAutoRenew nextTransactionAmount={nextTransactionAmount} />;
    default:
      return null;
  }
}

function AutoRenewal({ recurlyAccountStatus, nextTransactionAmount }: Props) {
  return (
    <CustomProvider>
      <AutoRenewalContent recurlyAccountStatus={recurlyAccountStatus} nextTransactionAmount={nextTransactionAmount} />
    </CustomProvider>
  );
}

export default AutoRenewal;
